/**
 * Central Identity OIDC Authentication Hook
 *
 * Implements OIDC Authorization Code + PKCE (S256) flow.
 * Calls Central directly - no backend proxy.
 *
 * Token Storage:
 * - Access token: MEMORY ONLY (Redux state)
 * - Refresh: Handled via Central session cookie (credentials:'include')
 * - localStorage is FORBIDDEN for tokens (XSS risk)
 */

import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// MODULE-LEVEL guard: every component that calls useCentralAuth() owns its
// own ref, so a per-component guard does NOT prevent the OIDC code from
// being exchanged multiple times when multiple components mount during the
// same callback render. An OIDC authorization code is single-use; the
// second attempt always fails with `invalid_grant`. Hoisting the guard to
// module scope makes the token exchange truly fire-once per page load.
let oidcCallbackHandled = false;
import type { RootState, AppDispatch } from '../store';
import {
  setCentralCredentials,
  centralLogout as centralLogoutAction,
  setAuthenticating,
  setCentralInitialized,
  selectCentralUser,
  selectCurrentCentralWorkspace,
  selectCentralWorkspaces,
  selectIsCentralAuthenticated,
  selectIsCentralInitialized,
  selectIsAuthenticating,
} from '../store/slices/centralAuthSlice';
import { api } from '../store/api';
import {
  retrieveVerifier,
  retrieveState,
  retrieveReturnUrl,
  clearOidcSession,
} from '../services/oidc/pkce';
import { peekPostLoginPath, setPostLoginPath } from '../services/oidc/returnPath';
import { signOut } from '../services/oidc/logout';
import {
  OIDC_ENDPOINTS,
  OIDC_CLIENT_ID,
  OIDC_REDIRECT_URI,
  CENTRAL_URL,
  initiateCentralLogin as initiateCentralLoginHelper,
} from '../config/centralAuth';

// ============================================================
// Types
// ============================================================

interface OidcTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  id_token?: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    selectedWorkspaceId?: string;
    workspaces: Array<{
      id: string;
      name: string;
      role: 'owner' | 'admin' | 'member' | 'viewer';
      isPrimary?: boolean;
    }>;
  };
}

// ============================================================
// Hook
// ============================================================

export function useCentralAuth() {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const user = useSelector(selectCentralUser);
  const workspace = useSelector(selectCurrentCentralWorkspace);
  const workspaces = useSelector(selectCentralWorkspaces);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const isInitialized = useSelector(selectIsCentralInitialized);
  const isAuthenticating = useSelector(selectIsAuthenticating);
  const accessToken = useSelector((state: RootState) => state.centralAuth.accessToken);

  /**
   * Initialize: Mark as initialized. In OIDC mode, we don't auto-refresh.
   *
   * Uses the MODULE-LEVEL `oidcCallbackHandled` flag so that only ONE of
   * the many components calling useCentralAuth() actually exchanges the
   * code — the rest short-circuit and just render. Otherwise an OIDC code
   * gets consumed by the first component and every subsequent attempt
   * fails with `invalid_grant`.
   */
  useEffect(() => {
    if (oidcCallbackHandled) return;
    oidcCallbackHandled = true;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      handleOidcCallback(code, state);
    } else {
      dispatch(setCentralInitialized());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle OIDC callback - exchange code for tokens.
   * Uses OIDC_ENDPOINTS.token from shared endpoint builders.
   */
  const handleOidcCallback = useCallback(
    async (code: string, state: string) => {
      dispatch(setAuthenticating(true));

      try {
        // Verify state to prevent CSRF
        const savedState = retrieveState();
        if (state !== savedState) {
          throw new Error('State mismatch - possible CSRF attack');
        }

        // Retrieve PKCE verifier
        const verifier = retrieveVerifier();
        if (!verifier) {
          throw new Error('Missing PKCE verifier');
        }

        // Exchange code for tokens (direct to Central)
        const response = await fetch(OIDC_ENDPOINTS.token, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: OIDC_CLIENT_ID,
            code,
            code_verifier: verifier,
            redirect_uri: OIDC_REDIRECT_URI,
          }),
        });

        if (!response.ok) {
          const error = await response.text();
          throw new Error(`Token exchange failed: ${error}`);
        }

        const tokens: OidcTokenResponse = await response.json();

        // Retrieve the deep link stored before the redirect, then clear session
        // data. It is validated (same-origin relative path only) and handed to
        // LoginPage, which performs the router navigation — replaceState alone
        // is invisible to React Router, which is why every fresh load used to
        // end on /dashboard.
        setPostLoginPath(retrieveReturnUrl());
        const returnUrl = peekPostLoginPath();

        // Clear OIDC session data
        clearOidcSession();

        // Clear URL params — show the return path, or the current path
        window.history.replaceState({}, document.title, returnUrl || window.location.pathname);

        // Extract user info — try multiple sources in order:
        // 1. Token response may include user object (Central extension)
        // 2. Decode id_token JWT claims (standard OIDC)
        // 3. Call /api/auth/me endpoint (Central's userinfo per discovery)
        let userData = tokens.user;

        if (!userData && tokens.id_token) {
          try {
            const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
            if (payload.sub && payload.email) {
              userData = {
                id: payload.sub,
                email: payload.email,
                name: payload.name,
                selectedWorkspaceId: payload.workspaceId,
                workspaces: [],
              };
            }
          } catch {
            // id_token decode failed — fall through to /me
          }
        }

        if (!userData) {
          const userResponse = await fetch(`${CENTRAL_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${tokens.access_token}`,
            },
          });
          if (userResponse.ok) {
            const meData = await userResponse.json();
            // Central /me returns { id, email, name, workspaces, ... }
            // or may be wrapped in { ok, user }
            userData = meData.user || meData;
          }
        }

        if (!userData) {
          throw new Error('Failed to get user information');
        }

        // Store credentials in memory (Redux state only)
        dispatch(
          setCentralCredentials({
            accessToken: tokens.access_token,
            refreshToken: '', // OIDC mode: no refresh token, uses Central session cookie
            expiresIn: tokens.expires_in ?? 3600,
            user: {
              id: userData.id,
              email: userData.email,
              fullName: userData.name ?? null,
              avatarUrl: null,
              centralAdmin: false,
              selectedWorkspaceId: userData.selectedWorkspaceId ?? null,
              workspaces: (userData.workspaces || []).map((ws) => ({
                id: ws.id,
                name: ws.name,
                slug: ws.id, // OIDC callback doesn't include slug; use id as fallback
                type: 'business' as const,
                role: ws.role,
                isPrimary: ws.isPrimary ?? false,
              })),
            },
          })
        );

        // `setCentralCredentials` lives in the shared slice; `isAuthenticating`
        // lives in BeHeard's app-specific extension — they don't cross.
        // Explicitly clear it so LoginPage's `if (isLoading) return` unblocks
        // the post-callback redirect to the intended destination.
        dispatch(setCentralInitialized());
        dispatch(setAuthenticating(false));
      } catch (error) {
        console.error('[OIDC] Callback error:', error);
        clearOidcSession();
        dispatch(setCentralInitialized());
        dispatch(setAuthenticating(false));
        // Redirect to login with error
        window.location.href = `/login?error=${encodeURIComponent(
          error instanceof Error ? error.message : 'Authentication failed'
        )}`;
      }
    },
    [dispatch]
  );

  /**
   * Initiate OIDC login flow.
   * Delegates to shared initiateCentralLogin helper (handles PKCE + redirect).
   */
  const initiateLogin = useCallback(async (returnTo?: string) => {
    dispatch(setAuthenticating(true));

    try {
      await initiateCentralLoginHelper(returnTo);
    } catch (error) {
      console.error('[OIDC] Login initiation error:', error);
      dispatch(setAuthenticating(false));
      throw error;
    }
  }, [dispatch]);

  /**
   * Logout: clear local state first (always), then end Central's SSO session
   * through its RP-initiated logout — a GET navigation, which is the only
   * logout method Central serves. See services/oidc/logout.ts.
   */
  const logout = useCallback(() => {
    signOut({
      centralUrl: CENTRAL_URL,
      clientId: OIDC_CLIENT_ID,
      oidcRedirectUri: OIDC_REDIRECT_URI,
      currentOrigin: window.location.origin,
      clearLocalSession: () => {
        dispatch(centralLogoutAction());
        dispatch(api.util.resetApiState());
        clearOidcSession();
      },
      navigate: (url) => window.location.assign(url),
    });
  }, [dispatch]);

  return {
    // State
    user,
    workspace,
    workspaces,
    isAuthenticated,
    isInitialized,
    isLoading: isAuthenticating,
    accessToken,

    // Actions
    logout,
    initiateLogin,
  };
}
